import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Save, Loader2, Building2, Upload, Trash2, Plus, Eye, FileText } from "lucide-react";
import api, { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import VendorDocuments from "@/components/VendorDocuments";
import TradeLicenseReminder from "@/components/TradeLicenseReminder";
import { TradeMultiSelect } from "@/components/TradeMultiSelect";
import { ChangePassword } from "@/components/ChangePassword";
import { DeviceSessions } from "@/components/DeviceSessions";
import { AutoQuoteBadgeToggle } from "@/components/AutoQuoteBadgeToggle";

export default function Settings() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({ trades: [], doc_types: [], vendor_types: [] });
  const [countries, setCountries] = useState([]);
  const [logoBust, setLogoBust] = useState(Date.now());
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = useRef();

  useEffect(() => {
    api.get("/profile/options").then((r) => setOptions(r.data)).catch(() => {});
    api.get("/public/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        company_name: user.company_name || "",
        address: user.address || "",
        phone: user.phone || "",
        contact_email: user.contact_email || "",
        tax_percent: user.tax_percent ?? 0,
        currency: user.currency || "$",
        vat_number: user.vat_number || "",
        peppol_id: user.peppol_id || "",
        invoice_prefix: user.invoice_prefix || "",
        terms: user.terms || "",
        quote_intro: user.quote_intro || "",
        bank_details: user.bank_details || "",
        signatory_name: user.signatory_name || "",
        signatory_title: user.signatory_title || "",
        business_types: user.business_types || [],
        vendor_type: user.vendor_type || "Supplier",
        trade_license_no: user.trade_license_no || "",
        website: user.website || "",
        year_established: user.year_established || "",
        description: user.description || "",
        city: user.city || "",
        country: user.country || "",
        public_listed: user.public_listed !== false,
        projects: user.projects || [],
      });
    }
  }, [user]);

  const peppolDigits = (form?.vat_number || "").replace(/\D/g, "");
  const peppolHint = peppolDigits.length >= 10 ? `0235:${peppolDigits.slice(0, 10)}` : "";

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tax_percent: parseFloat(form.tax_percent) || 0 };
      // Quotation intro / terms / bank details now live in Quotation Templates — don't overwrite here.
      delete payload.terms; delete payload.quote_intro; delete payload.bank_details;
      const { data } = await api.put("/profile", payload);
      setUser(data);
      toast.success(user?.account_type === "einvoice" ? "Company profile saved" : "Vendor profile saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/auth/me");
      setUser(data);
      setLogoBust(Date.now());
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploadingLogo(false);
      if (logoRef.current) logoRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    await api.delete("/profile/logo");
    const { data } = await api.get("/auth/me");
    setUser(data);
    toast.success("Logo removed");
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
  };

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isEinvoice = user?.account_type === "einvoice";

  const setProject = (i, k, v) =>
    setForm((f) => ({
      ...f,
      projects: f.projects.map((p, j) => (j === i ? { ...p, [k]: v } : p)),
    }));

  return (
    <div className="max-w-4xl">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Settings</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 dark:text-slate-100">{isEinvoice ? "Company Profile" : "Vendor Profile"}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isEinvoice ? "Your supplier details for e-invoices — company name, TRN, address, Peppol ID and logo." : "Used on your PDF quotations and on your public marketplace listing."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isEinvoice && user?.id && (
            <Button variant="outline" data-testid="preview-listing-button" onClick={() => window.open(`/vendor/${user.id}`, "_blank")} className="h-11 gap-2">
              <Eye className="w-4 h-4" /> Preview listing
            </Button>
          )}
          <Button data-testid="save-profile-button" onClick={save} disabled={saving} className="gap-2 h-11 font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Profile
          </Button>
        </div>
      </div>

      {!isEinvoice && <AutoQuoteBadgeToggle className="mb-6" />}

      <TradeLicenseReminder scrollToId="profile-documents" className="mb-6" />

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        {/* Identity */}
        <div className="bg-card border border-border rounded-xl shadow-sm">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-border">
            <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="font-semibold text-slate-900 dark:text-slate-100">Company Identity</div>
              <div className="text-xs text-muted-foreground">Logo, name and what you do</div>
            </div>
          </div>
          <div className="p-6 space-y-5">
            <div className="flex items-center gap-5">
              {user?.has_logo ? (
                <img
                  data-testid="logo-preview"
                  src={`${API}/public/vendors/${user.id}/logo?v=${logoBust}`}
                  alt="Company logo"
                  className="w-24 h-24 rounded-xl object-contain bg-white border border-border p-2"
                />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-secondary flex items-center justify-center">
                  <Building2 className="w-10 h-10 text-muted-foreground" />
                </div>
              )}
              <div>
                <input ref={logoRef} type="file" accept="image/*" hidden onChange={onLogo} data-testid="logo-file-input" />
                <div className="flex items-center gap-2">
                  <Button variant="outline" data-testid="upload-logo-button" onClick={() => logoRef.current?.click()} disabled={uploadingLogo} className="gap-2">
                    {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload logo
                  </Button>
                  {user?.has_logo && (
                    <Button variant="ghost" data-testid="remove-logo-button" onClick={removeLogo} className="gap-2 text-muted-foreground">
                      <Trash2 className="w-4 h-4" /> Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">PNG, JPG, WEBP or SVG · up to 10MB · {isEinvoice ? "shown on your e-invoices" : "shown on quotations and your listing"}</p>
              </div>
            </div>

            {!isEinvoice && (
            <div className="pt-2">
              <Label className="mb-1 block">Quotation supplements &amp; cover pages</Label>
              <p className="text-xs text-muted-foreground mb-3">
                Your intro, terms, bank details and cover/closing PDF pages now live in reusable
                <strong> Quotation Templates</strong> — create as many as you like and pick one in the Quote Builder.
              </p>
              <Button type="button" variant="outline" data-testid="settings-manage-templates" onClick={() => navigate("/quotation-templates")} className="gap-2">
                <FileText className="w-4 h-4" /> Manage quotation templates
              </Button>
            </div>
            )}

            <div>
              <Label>Company Name</Label>
              <Input data-testid="settings-company-name" value={form.company_name} onChange={set("company_name")} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {!isEinvoice && (
              <div>
                <Label>Vendor Type</Label>
                <Select value={form.vendor_type} onValueChange={(v) => setForm({ ...form, vendor_type: v })}>
                  <SelectTrigger data-testid="vendor-type-select" className="mt-1.5 bg-card"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">
                    {(options.vendor_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              )}
              <div>
                <Label>City</Label>
                <Input data-testid="settings-city" value={form.city} onChange={set("city")} className="mt-1.5" />
              </div>
              <div>
                <Label>Country</Label>
                <select data-testid="settings-country" value={form.country || ""}
                        onChange={set("country")}
                        className="mt-1.5 flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  <option value="">Select country</option>
                  {form.country && !countries.includes(form.country) && <option value={form.country}>{form.country}</option>}
                  {countries.map((cn) => <option key={cn} value={cn}>{cn}</option>)}
                </select>
              </div>
            </div>
            {!isEinvoice && (
            <div>
              <Label>About your company</Label>
              <Textarea data-testid="settings-description" value={form.description} onChange={set("description")} rows={3} className="mt-1.5" placeholder="What you supply or install, capacity, key clients..." />
            </div>
            )}
            {!isEinvoice && (
            <div>
              <Label className="mb-2 block">Type of business / trades</Label>
              <TradeMultiSelect
                options={options.trades || []}
                selected={form.business_types}
                onChange={(arr) => setForm({ ...form, business_types: arr })}
              />
            </div>
            )}
            {!isEinvoice && (
            <div className="flex items-center justify-between rounded-xl border border-border p-4">
              <div>
                <div className="text-sm font-semibold">List my company on the Matiqs marketplace</div>
                <div className="text-xs text-muted-foreground">Contractors can find you and copy your email to send an RFQ</div>
              </div>
              <Switch
                data-testid="public-listed-switch"
                checked={form.public_listed}
                onCheckedChange={(v) => setForm({ ...form, public_listed: v })}
              />
            </div>
            )}
          </div>
        </div>

        {/* Registration & contact */}
        <div className="bg-card border border-border rounded-xl shadow-sm">
          <div className="px-6 py-4 border-b border-border">
            <div className="font-semibold text-slate-900 dark:text-slate-100">{isEinvoice ? "Registration & Invoice Details" : "Registration & Quotation Defaults"}</div>
            <div className="text-xs text-muted-foreground">{isEinvoice ? "Appears on your e-invoices (supplier block)" : "Appears in the quotation header & footer"}</div>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <Label>Address</Label>
              <Textarea data-testid="settings-address" value={form.address} onChange={set("address")} rows={3} className="mt-1.5" placeholder="Street, City, Country, P.O. Box" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={set("phone")} className="mt-1.5" />
              </div>
              <div>
                <Label>{isEinvoice ? "Contact Email" : "Contact Email (used for RFQs)"}</Label>
                <Input data-testid="settings-contact-email" value={form.contact_email} onChange={set("contact_email")} className="mt-1.5" />
              </div>
              <div>
                <Label>Trade Licence No.</Label>
                <Input data-testid="settings-licence" value={form.trade_license_no} onChange={set("trade_license_no")} className="mt-1.5" />
              </div>
              <div>
                <Label>VAT / TRN Number</Label>
                <Input value={form.vat_number} onChange={set("vat_number")} className="mt-1.5" />
              </div>
              <div>
                <Label>Peppol ID (UAE e-invoicing)</Label>
                <Input data-testid="settings-peppol" value={form.peppol_id} onChange={set("peppol_id")}
                       className="mt-1.5 font-mono" placeholder={peppolHint || "0235:XXXXXXXXXX"} />
                <p className="text-[11px] text-muted-foreground mt-1">
                  {peppolHint ? `Auto-derived from your TRN: ${peppolHint}. Leave blank to use it, or type your own.`
                              : "Derived from your TRN (scheme 0235 + first 10 digits). Add your TRN first."}
                </p>
              </div>
              <div>
                <Label>Invoice number prefix</Label>
                <Input data-testid="settings-invoice-prefix" value={form.invoice_prefix} onChange={set("invoice_prefix")}
                       className="mt-1.5 font-mono" placeholder={`INV-${new Date().getFullYear()}-`} />
                <p className="text-[11px] text-muted-foreground mt-1">
                  e-invoices number sequentially, e.g. {(form.invoice_prefix || `INV-${new Date().getFullYear()}-`)}0001.
                </p>
              </div>
              {!isEinvoice && (
              <div>
                <Label>Website</Label>
                <Input data-testid="settings-website" value={form.website} onChange={set("website")} className="mt-1.5" placeholder="www.example.com" />
              </div>
              )}
              {!isEinvoice && (
              <div>
                <Label>Year Established</Label>
                <Input data-testid="settings-year" value={form.year_established} onChange={set("year_established")} className="mt-1.5" />
              </div>
              )}
              {!isEinvoice && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Currency</Label>
                  <Input data-testid="settings-currency" value={form.currency} onChange={set("currency")} className="mt-1.5" placeholder="$ / AED / ₹" />
                </div>
                <div>
                  <Label>Default Tax %</Label>
                  <Input data-testid="settings-tax" type="number" step="0.01" value={form.tax_percent} onChange={set("tax_percent")} className="mt-1.5 font-mono" />
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {!isEinvoice && (
        <>
        {/* Projects */}
        <div className="bg-card border border-border rounded-xl shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <div className="font-semibold text-slate-900 dark:text-slate-100">Projects</div>
              <div className="text-xs text-muted-foreground">Shown on your public listing to build trust</div>
            </div>
            <Button
              variant="outline"
              data-testid="add-project-button"
              onClick={() => setForm({ ...form, projects: [...form.projects, { name: "", client: "", year: "", value: "" }] })}
              className="gap-2"
            >
              <Plus className="w-4 h-4" /> Add project
            </Button>
          </div>
          <div className="p-6">
            {form.projects.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No projects added yet.</p>
            ) : (
              <div className="space-y-3">
                {form.projects.map((p, i) => (
                  <div key={i} data-testid={`project-row-${i}`} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                    <Input data-testid={`project-name-${i}`} value={p.name || ""} onChange={(e) => setProject(i, "name", e.target.value)} placeholder="Project name" className="sm:col-span-4" />
                    <Input value={p.client || ""} onChange={(e) => setProject(i, "client", e.target.value)} placeholder="Client" className="sm:col-span-3" />
                    <Input value={p.year || ""} onChange={(e) => setProject(i, "year", e.target.value)} placeholder="Year" className="sm:col-span-2" />
                    <Input value={p.value || ""} onChange={(e) => setProject(i, "value", e.target.value)} placeholder="Value" className="sm:col-span-2 font-mono" />
                    <button
                      data-testid={`project-delete-${i}`}
                      onClick={() => setForm({ ...form, projects: form.projects.filter((_, j) => j !== i) })}
                      className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors sm:col-span-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div id="profile-documents">
          <VendorDocuments docTypes={options.doc_types} />
        </div>
        </>
        )}

        <ChangePassword />

        <DeviceSessions />

        <div className="flex justify-end pb-4">
          <Button data-testid="save-profile-button-bottom" onClick={save} disabled={saving} className="gap-2 h-11 font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Profile
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
